import { useState, useEffect, useCallback, useRef } from "react";

function isDocumentFullscreen() {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
}

function requestDocumentFullscreen() {
  const el = document.documentElement;
  if (el.requestFullscreen) {
    return el.requestFullscreen();
  }
  if (el.webkitRequestFullscreen) {
    el.webkitRequestFullscreen();
    return Promise.resolve();
  }
  if (el.mozRequestFullScreen) {
    el.mozRequestFullScreen();
    return Promise.resolve();
  }
  if (el.msRequestFullscreen) {
    el.msRequestFullscreen();
    return Promise.resolve();
  }
  return Promise.reject(new Error("Fullscreen API unavailable"));
}

function exitDocumentFullscreen() {
  const d = document;
  if (d.exitFullscreen) {
    return d.exitFullscreen();
  }
  if (d.webkitExitFullscreen) {
    d.webkitExitFullscreen();
    return Promise.resolve();
  }
  if (d.mozCancelFullScreen) {
    d.mozCancelFullScreen();
    return Promise.resolve();
  }
  if (d.msExitFullscreen) {
    d.msExitFullscreen();
    return Promise.resolve();
  }
  return Promise.reject(new Error("Fullscreen API unavailable"));
}

/**
 * Browser Fullscreen API + CSS layout class sync.
 * CSS fullscreen applies even when the native API is unavailable (e.g. iOS).
 *
 * When the user leaves native fullscreen via the browser back gesture (not Esc
 * or the footer toggle), `onExitViaBrowserBack` runs so history can move back.
 */
export function useFullscreen({ onExitViaBrowserBack } = {}) {
  const [isFullscreen, setIsFullscreen] = useState(isDocumentFullscreen);
  const isFullscreenRef = useRef(isFullscreen);
  const userToggledRef = useRef(false);
  const escExitingRef = useRef(false);

  isFullscreenRef.current = isFullscreen;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        escExitingRef.current = true;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const docFs = isDocumentFullscreen();

      if (docFs) {
        setIsFullscreen(true);
        userToggledRef.current = false;
        return;
      }

      if (isFullscreenRef.current && !userToggledRef.current) {
        if (escExitingRef.current) {
          escExitingRef.current = false;
        } else {
          onExitViaBrowserBack?.();
        }
      }

      userToggledRef.current = false;
      setIsFullscreen(false);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange,
      );
    };
  }, [onExitViaBrowserBack]);

  const toggleFullscreen = useCallback(() => {
    userToggledRef.current = true;

    if (isFullscreenRef.current) {
      setIsFullscreen(false);
      if (isDocumentFullscreen()) {
        exitDocumentFullscreen().catch(() => {});
      }
      return;
    }

    setIsFullscreen(true);
    requestDocumentFullscreen().catch(() => {});
  }, []);

  return { isFullscreen, toggleFullscreen };
}
